import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import type {
  BookingSessionResponse,
  CreateBookingSessionRequest,
} from '../../types/booking-session.js'

import { apiClient } from '../../lib/client.js'
import { syncBookingSessionCache } from './cache.js'

const EXPIRED_SEARCH_SESSION_TEXT = 'session de recherche expirée'

interface ApiErrorResponse {
  message?: string
}

export const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof AxiosError)) {
    return undefined
  }

  const responseData = error.response?.data as ApiErrorResponse | undefined
  return typeof responseData?.message === 'string' ? responseData.message : undefined
}

export const isExpiredSearchSessionError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false
  }

  const status = error.response?.status
  if (status !== 400 && status !== 410) {
    return false
  }

  const message = getApiErrorMessage(error)
  return typeof message === 'string' && message.toLowerCase().includes(EXPIRED_SEARCH_SESSION_TEXT)
}

/**
 * True when the flight search failed because Affidium (our flight provider) is
 * down — the server answers HTTP 503 with errorCode 'PROVIDER_UNAVAILABLE'.
 * Lets the UI show a "maintenance" message instead of a generic error.
 */
export const isProviderUnavailableError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false
  }

  if (error.response?.status !== 503) {
    return false
  }

  return error.response?.data?.errorCode === 'PROVIDER_UNAVAILABLE'
}

/**
 * Discriminates the two 429 throttles emitted by the API:
 * - 'minute' → per-minute window (FlightSearchThrottle, ChatThrottle…)
 * - 'day'    → daily quota (DailySearchThrottlerGuard…)
 * Reads the structured `code` field set by the server guards; falls back to a
 * message pattern-match so older server versions still resolve correctly.
 */
export const getRateLimitScope = (error: unknown): 'day' | 'minute' | null => {
  if (!(error instanceof AxiosError) || error.response?.status !== 429) {
    return null
  }
  const data = error.response?.data as undefined | { code?: unknown; message?: unknown }
  if (data?.code === 'day' || data?.code === 'minute') return data.code
  const message = typeof data?.message === 'string' ? data.message.toLowerCase() : ''
  if (message.includes("aujourd'hui") || message.includes('demain') || message.includes('quotidien')) {
    return 'day'
  }
  if (message) return 'minute'
  return 'minute'
}

export const isPaymentAlreadyInitiatedError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false
  }

  if (error.response?.status !== 409) {
    return false
  }

  return error.response?.data?.errorCode === 'PAYMENT_ALREADY_INITIATED'
}

export const useCreateBookingSession = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateBookingSessionRequest) => {
      const response = await apiClient.client.post<BookingSessionResponse>('/booking-session', data)
      return response.data
    },
    onSuccess: (data) => {
      syncBookingSessionCache(queryClient, data)
    },
  })
}
